import { createContext, useContext, useEffect, useState } from "react";
import { dojo } from "@/plugins/vibeDojo/dojoAudio";

// Selectable Dojo scenes (art in /public).
export const SCENES = [
  { id: "zen", name: "Zen Rock Garden", src: "/dojo-zen-garden.jpg" },
  { id: "bamboo", name: "Bamboo Forest", src: "/dojo-bamboo.jpg" },
  { id: "koi", name: "Koi Pond", src: "/dojo-koi.jpg" },
  { id: "blossom", name: "Cherry Blossom", src: "/dojo-blossom.jpg" },
];

const KEY_MODE = "learnme_dojo_mode";
const KEY_SCENE = "learnme_dojo_scene";

// Dojo Mode is ONLY permitted on the entry + learning surfaces: the Landing page,
// the Course Creator (Dojo zen / Generate / guest demo), and the Course Player.
// On every other surface (ExpertMeAI, Admin, Library/Dashboard, Pricing, etc.) the
// header toggle is hidden and Dojo Mode is forced OFF.
export const isDojoAllowed = (p) =>
  p === "/" ||
  p === "/dojo" ||
  p === "/learn" ||
  p === "/demo" ||
  p === "/dashboard" ||
  p.startsWith("/course/") ||
  p.startsWith("/watch/") ||
  p.startsWith("/demo/course/");

const DojoContext = createContext(null);

export function DojoProvider({ children }) {
  // Always start OFF on any page load / refresh. Dojo Mode only turns on when
  // the user explicitly toggles it during the session (not restored from storage).
  const [dojoMode, setDojoModeState] = useState(false);
  const [sceneId, setSceneIdState] = useState(() => {
    try { return localStorage.getItem(KEY_SCENE) || "zen"; } catch { return "zen"; }
  });

  // Keep ambient audio in sync with the mode (no persistence of the on/off state).
  useEffect(() => {
    if (dojoMode) dojo.enable(); else dojo.disable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dojoMode]);

  const setDojoMode = (v) => {
    setDojoModeState(!!v);
    if (v) dojo.enable(); else dojo.disable();
  };
  const toggleDojo = () => setDojoMode(!dojoMode);
  const setScene = (id) => {
    setSceneIdState(id);
    try { localStorage.setItem(KEY_SCENE, id); } catch {}
  };

  const sceneSrc = (SCENES.find((s) => s.id === sceneId) || SCENES[0]).src;

  return (
    <DojoContext.Provider value={{ dojoMode, setDojoMode, toggleDojo, sceneId, setScene, sceneSrc, SCENES }}>
      {children}
    </DojoContext.Provider>
  );
}

export function useDojoMode() {
  const ctx = useContext(DojoContext);
  if (!ctx) throw new Error("useDojoMode must be used within DojoProvider");
  return ctx;
}
